import type { JSX } from "react";
import { Link } from "react-router-dom";

import { Footer } from "./Footer";
import { Grain } from "./Grain";
import { ProgressBar } from "./ProgressBar";
import { Reveal } from "./Reveal";
import { SkyBackground } from "./SkyBackground";

import { useDocumentMeta, useJsonLd } from "@/lib/seo";
import { toAsciiUpper } from "@/lib/time";

/** Anton lacks Turkish glyphs — strip diacritics and lowercase for the display. */
function asciiLower(text: string): string {
  return toAsciiUpper(text).toLowerCase();
}

/** Competition closes Sunday 9 August 2026 at 23:59 İzmir time. */
const COMPETITION_DEADLINE = new Date("2026-08-09T23:59:59+03:00");

const INSTAGRAM_URL = "https://instagram.com/noko.izmir";

const HOW_TO_STEPS = [
  "Head to the pinned giveaway post on @noko.izmir.",
  "Follow the entry steps in the first comment on that post.",
  "Follow @noko.izmir, like the post and tag a friend you would bring along. It helps more people find the giveaway, but none of it is a condition of winning.",
  "Get your entry in before Sunday 9 August 2026 at 23:59 İzmir time.",
];

const FINE_PRINT = [
  "The competition closes on Sunday 9 August 2026 at 23:59 İzmir time.",
  "Two winners are picked at random from all valid entries and announced on @noko.izmir.",
  "Each winner receives a pair of tickets to LP live in İzmir on Wednesday 12 August 2026.",
  "The prize is tickets only. Travel and accommodation are not included.",
  "This competition is not sponsored, endorsed or administered by, or associated with, Instagram.",
];

const FAQ = [
  {
    question: "HOW DO I ENTER?",
    answer:
      "Head to the pinned giveaway post on @noko.izmir and follow the entry steps in the first comment before Sunday 9 August 2026 at 23:59 İzmir time.",
  },
  {
    question: "WHEN DOES IT CLOSE?",
    answer:
      "Entries close on Sunday 9 August 2026 at 23:59 İzmir time. Anything after that does not count.",
  },
  {
    question: "HOW ARE WINNERS PICKED?",
    answer:
      "Two winners are picked at random from all valid entries. Each winner gets a pair of tickets to the show on 12 August.",
  },
  {
    question: "WHERE DO I HEAR IF I WON?",
    answer: "Winners are announced on @noko.izmir, so keep an eye on the account after 9 August.",
  },
];

const EVENT_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "Event",
  name: "LP live in İzmir",
  startDate: "2026-08-12",
  location: {
    "@type": "Place",
    name: "İzmir, Turkey",
    address: {
      "@type": "PostalAddress",
      addressLocality: "İzmir",
      addressCountry: "TR",
    },
  },
  organizer: {
    "@type": "Organization",
    name: "noko",
    url: "https://noko.city",
  },
};

const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

function SectionLabel({ children }: { children: string }): JSX.Element {
  return (
    <p className="font-mono text-[clamp(0.65rem,1.5vw,1rem)] font-semibold uppercase tracking-[0.2em] text-accent">
      {children}
    </p>
  );
}

/** Accent-bar CTA — primary enters on Instagram, secondary links to the guide. */
function AccentCta({
  href,
  label,
  external = false,
}: {
  href: string;
  label: string;
  external?: boolean;
}): JSX.Element {
  const inner = (
    <>
      <div className="h-2 w-[72px] bg-accent" />
      <span className="mt-3 block font-mono text-[clamp(0.75rem,1.8vw,1.25rem)] font-semibold tracking-wider text-paper transition-colors group-hover:text-accent">
        {label}
      </span>
    </>
  );
  return external ? (
    <a href={href} target="_blank" rel="noreferrer" className="group inline-block">
      {inner}
    </a>
  ) : (
    // The one in-site target is a guide, and a guide is a prerendered document
    // (its body and its payload are the same file) — a router `Link` would walk
    // the SPA into a URL it has no body for. See `lib/guide-document.ts`.
    <a href={href} className="group inline-block">
      {inner}
    </a>
  );
}

/**
 * Static LP giveaway campaign page. Self-authored marketing content already
 * public on @noko.izmir — no event data, no API calls. Renders a closed
 * variant automatically after COMPETITION_DEADLINE.
 */
export function LpGiveawayPage(): JSX.Element {
  const closed = Date.now() > COMPETITION_DEADLINE.getTime();

  useDocumentMeta({
    title: "LP live in İzmir: win a pair of tickets | noko",
    description:
      "LP plays İzmir on 12 August 2026 and noko is giving away tickets. Enter on Instagram at @noko.izmir before Sunday 9 August for the chance to win a pair.",
    canonical: "https://noko.city/page/izmir/lp",
    ogTitle: "LP live in İzmir: win a pair of tickets",
    ogDescription:
      "LP plays İzmir on 12 August 2026 and noko is giving away tickets. Enter on Instagram at @noko.izmir before Sunday 9 August for the chance to win a pair.",
    ogUrl: "https://noko.city/page/izmir/lp",
    ogImage: "/og.jpg",
  });
  useJsonLd("event", EVENT_JSON_LD);
  useJsonLd("faq", FAQ_JSON_LD);

  return (
    <div className="relative min-h-screen">
      <SkyBackground />
      <Grain />
      <ProgressBar />

      {/* Top bar — wordmark + back to all cities. */}
      <div className="relative z-10 mx-auto flex w-full max-w-[1400px] items-baseline justify-between px-[6%] pt-6">
        <Link
          to="/"
          className="font-anton text-[clamp(1.4rem,3vw,2.2rem)] lowercase leading-none text-paper"
        >
          noko
        </Link>
        <Link
          to="/"
          className="font-mono text-[clamp(0.6rem,1.4vw,0.9rem)] font-semibold tracking-wider text-paper-muted transition-colors hover:text-accent"
        >
          ALL CITIES
        </Link>
      </div>

      <main className="relative z-10 mx-auto w-full max-w-[1400px]">
        {/* 1. Hero */}
        <section className="px-[6%] pt-[8vh]">
          <Reveal variant="rise">
            <h1 className="font-anton lowercase leading-none text-paper text-[clamp(3rem,14vw,11rem)]">
              {asciiLower("LP")}
            </h1>
          </Reveal>
          <Reveal variant="stamp" delay={120}>
            <p className="pt-3 font-mono text-[clamp(0.75rem,1.8vw,1.25rem)] font-semibold tracking-[0.2em] text-paper-muted">
              LIVE IN İZMIR · 12 AUGUST
            </p>
            {closed ? (
              <>
                <p className="pt-8 font-anton uppercase leading-none text-accent text-[clamp(1.8rem,6vw,4.5rem)]">
                  COMPETITION CLOSED
                </p>
                <p className="max-w-[640px] pt-4 font-roboto text-[clamp(1rem,2vw,1.3rem)] leading-relaxed text-paper-header">
                  This giveaway closed on Sunday 9 August 2026. The winners were picked at random
                  from all valid entries and announced on @noko.izmir.
                </p>
                <p className="pt-4 font-mono text-[clamp(0.7rem,1.5vw,1rem)] font-semibold tracking-wider text-paper-header">
                  WINNERS ANNOUNCED ON @NOKO.IZMIR
                </p>
                <div className="flex flex-wrap gap-x-10 gap-y-6 pt-6">
                  <AccentCta href={INSTAGRAM_URL} label="SEE THE WINNERS ON @NOKO.IZMIR" external />
                  <AccentCta href="/page/izmir" label="THE İZMIR GUIDE" />
                </div>
              </>
            ) : (
              <>
                <p className="pt-8 font-anton uppercase leading-none text-paper text-[clamp(1.8rem,6vw,4.5rem)]">
                  WIN A PAIR OF TICKETS
                </p>
                <p className="pt-3 font-mono text-[clamp(0.7rem,1.5vw,1rem)] font-semibold tracking-wider text-paper-muted">
                  TWO WINNERS · A PAIR EACH · CLOSES SUNDAY 9 AUGUST 2026
                </p>
                <div className="flex flex-wrap gap-x-10 gap-y-6 pt-6">
                  <AccentCta href={INSTAGRAM_URL} label="FOLLOW @NOKO.IZMIR TO ENTER" external />
                  <AccentCta href="/page/izmir" label="THE İZMIR GUIDE" />
                </div>
              </>
            )}
          </Reveal>
        </section>

        {/* 2. How to take part — open state only. */}
        {!closed && (
          <Reveal variant="stamp" delay={160}>
            <section className="px-[6%] pt-[10vh]">
              <div className="border-t border-white/15 py-[6vh]">
                <SectionLabel>HOW TO TAKE PART</SectionLabel>
                <ol className="grid grid-cols-1 gap-6 pt-6 md:grid-cols-2">
                  {HOW_TO_STEPS.map((step, i) => (
                    <li key={step} className="border border-white/20 p-5">
                      <p className="font-anton leading-none text-accent text-[clamp(1.4rem,3vw,2rem)]">
                        {String(i + 1).padStart(2, "0")}
                      </p>
                      <p className="pt-3 font-roboto text-[clamp(0.95rem,1.8vw,1.2rem)] leading-relaxed text-paper-header">
                        {step}
                      </p>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          </Reveal>
        )}

        {/* 3. The fine print. */}
        <Reveal variant="stamp" delay={200}>
          <section className="px-[6%] pt-[4vh]">
            <div className="border-t border-white/15 py-[6vh]">
              <SectionLabel>THE FINE PRINT</SectionLabel>
              <ul className="max-w-[760px] space-y-4 pt-6">
                {FINE_PRINT.map((line) => (
                  <li
                    key={line}
                    className="font-roboto text-[clamp(0.9rem,1.7vw,1.1rem)] leading-relaxed text-paper-muted"
                  >
                    <span aria-hidden className="mr-3 text-accent">
                      ·
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* 4. FAQ — visible, mirrors the FAQPage JSON-LD. */}
        <Reveal variant="stamp" delay={240}>
          <section className="px-[6%] pt-[4vh] pb-[6vh]">
            <div className="border-t border-white/15 py-[6vh]">
              <SectionLabel>QUESTIONS</SectionLabel>
              <dl className="grid grid-cols-1 gap-8 pt-6 md:grid-cols-2">
                {FAQ.map(({ question, answer }) => (
                  <div key={question}>
                    <dt className="font-mono text-[clamp(0.75rem,1.6vw,1rem)] font-semibold tracking-wider text-paper">
                      {question}
                    </dt>
                    <dd className="pt-2 font-roboto text-[clamp(0.95rem,1.8vw,1.2rem)] leading-relaxed text-paper-header">
                      {answer}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </section>
        </Reveal>

        {/* 5. Footer CTA. */}
        <Reveal variant="stamp" delay={280}>
          <section className="px-[6%] pb-[4vh]">
            <p className="font-mono text-[clamp(0.7rem,1.5vw,1rem)] font-semibold tracking-wider text-paper-muted">
              {closed ? "THE GUIDE CARRIES ON" : "TWICE A WEEK, EVERY WEEK"}
            </p>
            <div className="flex flex-wrap gap-x-10 gap-y-6 pt-6">
              <AccentCta
                href={INSTAGRAM_URL}
                label={closed ? "FOLLOW @NOKO.IZMIR" : "FOLLOW @NOKO.IZMIR TO ENTER"}
                external
              />
              <AccentCta href="/page/izmir" label="THE İZMIR GUIDE" />
            </div>
          </section>
        </Reveal>
      </main>

      <div className="relative z-10">
        <Footer instagramHref={INSTAGRAM_URL} instagramLabel="@NOKO.IZMIR" />
      </div>
    </div>
  );
}
